import { useCallback, useEffect, useRef, useState } from 'react';
import type { Page } from './admin';

export interface InfiniteList<T> {
  items: T[];
  loading: boolean;
  error: Error | null;
  hasMore: boolean;
  /** Attach to a sentinel element rendered after the last row. */
  sentinelRef: (node: HTMLElement | null) => void;
  loadMore: () => void;
  reset: () => void;
}

/**
 * Cursor-paginated infinite list driven by an IntersectionObserver.
 *
 * An observer beats a scroll listener here: no throttling to tune, no layout
 * thrash from reading scrollHeight, and it works inside any scroll container.
 *
 * `fetchPage` must be stable (useCallback). When the query behind it changes,
 * the list resets and reloads from the first page — otherwise filtered results
 * would be appended onto unfiltered ones.
 */
export function useInfiniteList<T>(
  fetchPage: (cursor: string | null) => Promise<Page<T>>,
): InfiniteList<T> {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(true);

  // Mirrored into refs so the observer callback always reads current values
  // without having to re-subscribe on every state change.
  const cursorRef = useRef<string | null>(null);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const observerRef = useRef<IntersectionObserver | null>(null);
  // Discards responses from a previous query after reset().
  const generation = useRef(0);

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMoreRef.current) return;
    const current = generation.current;

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const page = await fetchPage(cursorRef.current);
      if (current !== generation.current) return; // superseded by a reset
      setItems((prev) => [...prev, ...page.items]);
      cursorRef.current = page.nextCursor;
      hasMoreRef.current = page.nextCursor !== null;
      setHasMore(page.nextCursor !== null);
    } catch (err) {
      if (current !== generation.current) return;
      setError(err instanceof Error ? err : new Error(String(err)));
      // Stop paging on error so a failing endpoint isn't hammered by the
      // observer every time the sentinel re-enters the viewport.
      hasMoreRef.current = false;
      setHasMore(false);
    } finally {
      if (current === generation.current) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  }, [fetchPage]);

  const reset = useCallback(() => {
    generation.current += 1;
    cursorRef.current = null;
    loadingRef.current = false;
    hasMoreRef.current = true;
    setItems([]);
    setHasMore(true);
    setError(null);
    setLoading(false);
  }, []);

  // A new fetchPage means a new query: start over.
  useEffect(() => {
    reset();
  }, [fetchPage, reset]);

  // Load the first page whenever the list is empty and more is available.
  useEffect(() => {
    if (items.length === 0 && hasMore && !loadingRef.current) {
      void loadMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, hasMore, loadMore]);

  // Callback ref rather than useRef + useEffect, so the observer is rebuilt
  // whenever loadMore changes and never closes over a stale one.
  const sentinelRef = useCallback(
    (node: HTMLElement | null) => {
      observerRef.current?.disconnect();
      if (!node) return;
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) void loadMore();
        },
        // Start fetching before the user actually reaches the bottom.
        { rootMargin: '200px' },
      );
      observerRef.current.observe(node);
    },
    [loadMore],
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return { items, loading, error, hasMore, sentinelRef, loadMore, reset };
}
